import axios from "axios"
const baseUrl = process.env.REACT_APP_API_BASEURL

/**
 * @function generateCertificateService
 * @param ({ authToken, SubmissionId, certificateData })
 * @description Generate and store certificate in database, returns verification code and URL
 */
export const generateCertificateService = async ({ authToken, SubmissionId, certificateData }) => {
    const result = await axios.post(`${baseUrl}certificates/generate`, {
        SubmissionId,
        certificateData
    }, {
        headers: {
            Authorization: `Bearer ${authToken}`
        }
    })
    return result
}

/**
 * @function getCertificateByVerificationCodeService
 * @param ({ verificationCode })
 * @description Get certificate data by verification code (public endpoint, no auth required)
 */
export const getCertificateByVerificationCodeService = async ({ verificationCode }) => {
    const result = await axios.get(`${baseUrl}certificates/verify/${verificationCode}`)
    return result
}

/**
 * @function getCertificateBySubmissionIdService
 * @param ({ authToken, SubmissionId })
 * @description Get certificate for a specific submission (authenticated)
 */
export const getCertificateBySubmissionIdService = async ({ authToken, SubmissionId }) => {
    const result = await axios.get(`${baseUrl}certificates/submission/${SubmissionId}`, {
        headers: {
            Authorization: `Bearer ${authToken}`
        }
    })
    return result
}

/**
 * @function checkCertificateApprovalStatusService
 * @param ({ authToken, SubmissionId })
 * @description Check if certificate is approved for a submission (authenticated)
 */
export const checkCertificateApprovalStatusService = async ({ authToken, SubmissionId }) => {
    const result = await axios.get(`${baseUrl}certificates/approval-status/${SubmissionId}`, {
        headers: {
            Authorization: `Bearer ${authToken}`
        }
    })
    return result
}

/**
 * @function getAllCertificatesService
 * @param ({ authToken, offset, limit, search })
 * @description List all certificates (Admin only)
 */
export const getAllCertificatesService = async ({ authToken, offset = 0, limit = 10, search = '' }) => {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    const result = await axios.get(
        `${baseUrl}certificates/admin/list?offset=${offset}&limit=${limit}${searchParam}`,
        {
            headers: {
                Authorization: `Bearer ${authToken}`
            }
        }
    )
    return result
}

/**
 * @function getCertificateByIdService
 * @param ({ authToken, certificateId })
 * @description Get certificate by ID (Admin only)
 */
export const getCertificateByIdService = async ({ authToken, certificateId }) => {
    const result = await axios.get(`${baseUrl}certificates/admin/${certificateId}`, {
        headers: {
            Authorization: `Bearer ${authToken}`
        }
    })
    return result
}

/**
 * @function approveCertificateService
 * @param ({ authToken, certificateId })
 * @description Approve a certificate (Admin only)
 */
export const approveCertificateService = async ({ authToken, certificateId }) => {
    const result = await axios.patch(
        `${baseUrl}certificates/${certificateId}/approve`,
        {},
        {
            headers: {
                Authorization: `Bearer ${authToken}`
            }
        }
    )
    return result
}

/**
 * @function rejectCertificateService
 * @param ({ authToken, certificateId, rejectionReason })
 * @description Reject a certificate (Admin only)
 */
export const rejectCertificateService = async ({ authToken, certificateId, rejectionReason }) => {
    const result = await axios.patch(
        `${baseUrl}certificates/${certificateId}/reject`,
        { rejectionReason },
        {
            headers: {
                Authorization: `Bearer ${authToken}`
            }
        }
    )
    return result
}

/**
 * @function getPendingCertificatesService
 * @param ({ authToken, offset, limit })
 * @description List pending certificates (Admin only)
 */
export const getPendingCertificatesService = async ({ authToken, offset = 0, limit = 10 }) => {
    const result = await axios.get(
        `${baseUrl}certificates/pending?offset=${offset}&limit=${limit}`,
        {
            headers: {
                Authorization: `Bearer ${authToken}`
            }
        }
    )
    return result
}

/**
 * @function getSubmissionsNeedingCertificatesService
 * @param ({ authToken, offset, limit, search })
 * @description Get submissions that need certificates (Admin only)
 */
export const getSubmissionsNeedingCertificatesService = async ({ authToken, offset = 0, limit = 10, search = '' }) => {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    const result = await axios.get(
        `${baseUrl}certificates/submissions/needing-certificates?offset=${offset}&limit=${limit}${searchParam}`,
        {
            headers: {
                Authorization: `Bearer ${authToken}`
            }
        }
    )
    return result
}

